// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateVariantSelection from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/UpdateVariantSelection";
import { VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { useLocation } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

const useVariantParams = (parentProductId: string, variantTraits: VariantTraitModel[]) => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const variantSelection = useSelector((state: ApplicationState) => state.pages.productDetails.variantSelection);
    const isInitialLoad = useRef(true);
    const previousPathName = usePrevious(location.pathname);

    const getTraitById = (id: string) => variantTraits.find(trait => trait.id === id);
    const getTraitByName = (name: string) => variantTraits.find(trait => trait.name === name);

    const getTraitValueById = (traitId: string, valueId: string) => {
        const trait = getTraitById(traitId);
        return trait?.traitValues?.find(value => value.id === valueId);
    };

    const getTraitValueByName = (traitName: string, valueDisplay: string) => {
        const trait = getTraitByName(traitName);
        return trait?.traitValues?.find(value => value.valueDisplay === valueDisplay);
    };

    const decodeVariantTraitsFromQuery = (): Record<string, string> => {
        const params = new URLSearchParams(location.search);
        const decodedTraits: Record<string, string> = {};
        params.forEach((value, key) => {
            const traitName = decodeURIComponent(key);
            const valueDisplay = decodeURIComponent(value);

            const trait = getTraitByName(traitName);
            const traitValue = getTraitValueByName(traitName, valueDisplay);

            if (trait && traitValue) {
                decodedTraits[trait.id] = traitValue.id;
            }
        });
        return decodedTraits;
    };

    useEffect(() => {
        if (isInitialLoad.current) {
            const variantParams = decodeVariantTraitsFromQuery();
            if (Object.keys(variantParams).length > 0) {
                Object.entries(variantParams).forEach(([variantTraitId, traitValueId]) => {
                    dispatch(
                        updateVariantSelection({
                            variantTraitId,
                            traitValueId,
                            productId: parentProductId,
                        }),
                    );
                });
            }
            isInitialLoad.current = false;
        }
    }, [dispatch, parentProductId, location.search]);

    useEffect(() => {
        if (isInitialLoad.current || !variantSelection) {
            return;
        }
        if (previousPathName !== location.pathname) {
            return;
        }

        const queryString = new URLSearchParams();
        Object.entries(variantSelection).forEach(([variantTraitId, traitValueId]) => {
            const trait = getTraitById(variantTraitId);
            const traitValue = getTraitValueById(variantTraitId, traitValueId ?? "");
            if (trait && traitValue) {
                queryString.append(encodeURIComponent(trait.name), encodeURIComponent(traitValue.valueDisplay));
            }
        });

        history.replace(`${location.pathname}${queryString.toString().length > 0 ? `?${queryString.toString()}` : ""}`);
    }, [variantSelection, history, location.pathname]);
};

export default useVariantParams;
